import {chain, isUndefined, sortBy, union, unionBy} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useCallback, useMemo, useState} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useInitialEffect} from '../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {CancelError} from '../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {ApiSearchInboxes} from '../../../../libs/shared/core/src/models/api/searchInboxModel';
import {buildInboxSearchUrl} from '../../../../libs/shared/core/src/models/helpers/inboxHelpers';
import {reportToBugsnag} from '../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {HttpError} from '../../../../libs/shared/core/src/services/http/httpError';
import {
  computeDropdownBannerHeight,
  DropdownBanner,
} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownBanner';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemInboxDot} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemInboxDot';
import {Skeleton} from '../../../../libs/shared/ui/src/components/utility/skeleton';
import {filterRenderers} from '../../../analytics/helpers/analyticsFiltersHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {ApiLensDataGroup} from '../../../models/api/dataGroupModel';
import {DropdownFetcher} from '../../common/dropdowns/source/asyncDropdownSource';
import {FilterRowGenerator} from '../../common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {DisplayLensIngredient} from '../../inboxes/workspaces/lensesWorkspace/helpers/useLensValidation';
import {LensFormInboxesMap} from './lensFormMultiSelectInboxesContainer';
import {LensFormMultiselectProps} from './types';

/*
 * Constants.
 */

const fetchInboxesPageSize = 50;
const defaultSelectInboxesDropdownBannerHeight = computeDropdownBannerHeight(2);

/*
 * Props.
 */

interface LensFormMultiselectInboxesProps extends LensFormMultiselectProps<InboxModel> {
  namespace: string | undefined;
  shouldLoadAllInboxes: boolean;
  companyUrl: string;
  selectedInboxesInState: LensFormInboxesMap;
  updateInboxesInState: (arg: ReadonlyArray<InboxModel>) => void;
  lensId: number | undefined;
  displayAccessWarningBanner?: boolean;
  deletedInboxes?: ReadonlyArray<DisplayLensIngredient>;
}

/*
 * Component.
 */

export const LensFormMultiSelectInboxes: FC<LensFormMultiselectInboxesProps> = (props) => {
  const {
    isDisabled,
    choices,
    selectedIds,
    onSetItemIds,
    selectedInboxesInState,
    companyUrl,
    updateInboxesInState,
    shouldLoadAllInboxes,
    namespace,
    lensId,
    displayAccessWarningBanner,
    deletedInboxes,
  } = props;
  const [selectedInboxesAsyncStatus, setSelectedInboxesAsyncStatus] = useState<AsyncStatusesEnum>(
    AsyncStatusesEnum.LOADING,
  );
  // Inboxes that are available for rendering the dropdown button value.
  const [inboxesMap, setInboxesMap] = useState<LensFormInboxesMap>({...selectedInboxesInState});

  // Loading the selected inboxes in case they aren't already present in state.
  useInitialEffect(() => {
    // If all the selected inboxes are already in state, nothing to do!
    if (selectedIds.every((id) => !isUndefined(inboxesMap[id]))) {
      setSelectedInboxesAsyncStatus(AsyncStatusesEnum.LOADED);
      return;
    }

    // Fetch the lens to get inboxes not currently in state.
    const fetchInboxesFromLens = async () => {
      // If lensId is undefined, then the user is creating a new lens, and
      // there isn't anything we need to fetch.
      if (lensId) {
        try {
          const {inboxes} = await fetchApiAsync(ApiLensDataGroup, `${companyUrl}/lenses/${lensId}`);
          const loadedInboxes = inboxes?.reduce<LensFormInboxesMap>(
            (prev, inbox) => ({...prev, [inbox.id]: inbox}),
            {},
          );
          // Add any missing inboxes into the state.
          if (inboxes && inboxes.length > 0) {
            updateInboxesInState(inboxes);
          }
          setInboxesMap((prevInboxesMap) => ({...prevInboxesMap, ...loadedInboxes}));
          setSelectedInboxesAsyncStatus(AsyncStatusesEnum.LOADED);
        } catch (error) {
          if (error instanceof CancelError) {
            return;
          }

          if (!(error instanceof HttpError)) {
            reportToBugsnag(error);
          }
          setSelectedInboxesAsyncStatus(AsyncStatusesEnum.FAILED);
        }
      }
    };

    fetchInboxesFromLens();
  });

  const deletedInboxIds = deletedInboxes?.map((inbox) => inbox.id);
  const filteredSelectedIds = !deletedInboxIds
    ? selectedIds
    : selectedIds.filter((id) => !deletedInboxIds.includes(id));

  const selectedInboxes = chain(filteredSelectedIds)
    .map((id) => inboxesMap[id])
    .compact()
    .value();

  const dropdownSource = useMemo(() => {
    if (!shouldLoadAllInboxes) {
      // Just use the user accessible inboxes from state
      const fetcher = buildFetcher(choices, filteredSelectedIds);
      return computeFilterDropdownSourceAsync(fetcher);
    }

    // Make an async fetcher to get _all_ the current inboxes from
    // the namespace, since the user is an admin editing shared views.
    return computeFilterDropdownSourceAsync(
      buildAdminFetcher(namespace, companyUrl, selectedInboxes, Boolean(displayAccessWarningBanner)),
    );
  }, [
    shouldLoadAllInboxes,
    namespace,
    companyUrl,
    selectedInboxes,
    displayAccessWarningBanner,
    choices,
    filteredSelectedIds,
  ]);

  const buildRowGenerator: FilterRowGenerator<InboxModel | null> = useCallback(
    (inbox, dropdownSelectedIds, makeOnItemSelect) => {
      if (inbox === null) {
        return {
          isException: true,
          height: defaultSelectInboxesDropdownBannerHeight,
          renderer: () => (
            <DropdownBanner>
              <Trans id="lens-form-multi-select-inboxes-admin-access-warning">
                Adding inboxes may remove a teammate&apos;s access to this view
              </Trans>
            </DropdownBanner>
          ),
        };
      }

      const isSelected = dropdownSelectedIds.includes(inbox.id);

      const onItemSelect = makeOnItemSelect(inbox.id);
      const onSelect = () => {
        setInboxesMap((prevInboxesMap) => ({
          ...prevInboxesMap,
          [inbox.id]: inbox,
        }));
        onItemSelect();
      };

      return {
        isSelected,
        renderer: () => (
          <>
            <DropdownItemInboxDot inbox={inbox} />
            <DropdownItemContent isChecked={isSelected}>{inbox.name}</DropdownItemContent>
          </>
        ),
        onSelect,
        id: inbox.id,
      };
    },
    [],
  );

  return (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={selectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={(items) => {
        const filteredItems = items.filter((item) => !deletedInboxIds?.includes(item));
        onSetItemIds(filteredItems);
      }}
      onClearAll={() => onSetItemIds([])}
      renderValue={() =>
        renderValue(
          selectedInboxesAsyncStatus,
          chain(selectedIds)
            .map((id) => inboxesMap[id])
            .compact()
            .value(),
        )
      }
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-inboxes-placeholder">Select shared inboxes</Trans>
      )}
      label={props.label || <Trans id="lens-form-multiselect-inboxes-label">In any of these inboxes</Trans>}
      dropdownTitle={<Trans id="lens-form-multiselect-inboxes-dropdown-title">Shared inboxes</Trans>}
      isDisabled={isDisabled}
      isRequired
      messageForDisabled={
        <Trans id="lens-form-multiselect-inboxes-disabled-message">
          Field will be enabled once a workspace is selected.
        </Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );
};

/*
 * Helpers.
 */

const filterRowsByQuery = memoize(20)((choices: ReadonlyArray<InboxModel>, searchQuery: string) => {
  // For empty query, render all choices.
  if (!searchQuery) {
    return choices;
  }

  // Otherwise, filter by name.
  const lowerCaseQuery = searchQuery.toLowerCase();
  return chain(choices)
    .filter(({name}) => name.toLowerCase().includes(lowerCaseQuery))
    .value();
});

function renderValue(
  selectedInboxesAsyncStatus: AsyncStatusesEnum,
  selectedInboxes: ReadonlyArray<InboxModel>,
) {
  if (selectedInboxesAsyncStatus === AsyncStatusesEnum.LOADING) {
    return <Skeleton height="20px" width="200px" />;
  }

  if (!selectedInboxes.length) {
    return null;
  }

  return (
    <>
      {intersperseNodes(
        selectedInboxes.map((inbox) => ({
          node: filterRenderers[AnalyticsFilterTypesEnum.INBOXES].renderItem(inbox),
          key: String(inbox.id),
        })),
        () => ', ',
      )}
    </>
  );
}

function buildFetcher(
  choices: LensFormMultiselectInboxesProps['choices'],
  initialSelectedIds: ReadonlyArray<number>,
): DropdownFetcher<InboxModel> {
  return async (query) => {
    const items = filterRowsByQuery(choices, query);

    return {
      items:
        initialSelectedIds.length > 0 ? sortBy(items, (row) => !initialSelectedIds.includes(row.id)) : items,
      canLoadMore: false,
    };
  };
}

function buildAdminFetcher(
  namespace: string | undefined,
  companyUrl: string,
  selectedInboxes: ReadonlyArray<InboxModel>,
  displayAccessWarningBanner: boolean,
): DropdownFetcher<InboxModel | null> {
  return async (query, inboxes, cancelToken, paginationData) => {
    // Build the URL and options.
    const nextPageIndex = paginationData?.pageIndex || 0;
    const url = buildInboxSearchUrl(companyUrl, query, {
      offset: nextPageIndex * fetchInboxesPageSize,
      limit: fetchInboxesPageSize,
      shouldIncludePrivate: true,
      namespace,
    });

    // Fetch the inboxes.
    const fetchedInboxes = await fetchApiAsync(ApiSearchInboxes, url, {cancelToken});
    const canLoadMore = fetchedInboxes.inboxes.length === fetchedInboxes.limit;

    // If we aren't searching, we want to show the selected items
    // at the top of the dropdown.
    const allInboxes =
      query.length > 0
        ? unionBy(inboxes, fetchedInboxes.inboxes, 'id')
        : unionBy(selectedInboxes, inboxes, fetchedInboxes.inboxes, 'id');

    // null renders an info banner in the dropdown which warns that editing the view's inboxes can change
    // teammate access. We don't want to show the banner in the case where there's empty inbox search results.
    const items =
      displayAccessWarningBanner && allInboxes.length > 0 ? union([null], allInboxes) : allInboxes;

    return {
      items,
      canLoadMore,
      paginationData: {
        pageIndex: nextPageIndex + 1,
        pageSize: fetchInboxesPageSize,
      },
    };
  };
}
